import { 
  AIIdeaInput, 
  BusinessIdea, 
  BusinessIdeaKit,
  SurveyQuestion, 
  TaskItem, 
  FallbackIdeaTemplate,
  RevenueModel,
  MarketAnalysis,
  ResourceRequirements,
  LaunchGuide
} from '../../types/ai';

/**
 * Fallback Provider Service
 * Provides fallback data when AI services are unavailable
 */
export class FallbackProvider {
  /**
   * Get fallback business kits when AI is unavailable
   * Returns complete BusinessIdeaKit objects with all enhancement fields
   */
  static getFallbackBusinessKits(input: AIIdeaInput, language: string = 'en'): BusinessIdeaKit[] {
    const templates = language === 'zh' 
      ? this.getFallbackKitTemplatesZh() 
      : this.getFallbackKitTemplates();

    // Match templates based on skills, interests, or problem type
    const matchingTemplates = templates.filter(template =>
      input.skills.some(skill => template.skills.includes(skill)) ||
      input.interests.some(interest => template.interests.includes(interest)) ||
      (template.problemTypes && template.problemTypes.includes(input.problemType))
    );

    const selectedKits: BusinessIdeaKit[] = [];
    for (const template of matchingTemplates) {
      if (template.kits && Array.isArray(template.kits)) {
        selectedKits.push(...template.kits);
      }
    }

    // If we don't have enough matching kits, add default ones
    if (selectedKits.length < 3) {
      const defaultKits = language === 'zh' 
        ? this.getDefaultFallbackBusinessKitsZh() 
        : this.getDefaultFallbackBusinessKits();
      selectedKits.push(...defaultKits);
    }

    return selectedKits.slice(0, 3);
  }

  /**
   * Get fallback ideas when AI is unavailable (legacy method for backward compatibility)
   */
  static getFallbackIdeas(input: AIIdeaInput): BusinessIdea[] {
    const templates = this.getFallbackTemplates();

    const matchingTemplates = templates.filter(template =>
      input.skills.some(skill => template.skills.includes(skill)) ||
      input.interests.some(interest => template.interests.includes(interest))
    );

    const selectedIdeas: BusinessIdea[] = [];
    for (const template of matchingTemplates) {
      if (template.ideas && Array.isArray(template.ideas)) {
        selectedIdeas.push(...template.ideas);
      }
    }

    if (selectedIdeas.length < 3) {
      const defaultIdeas = this.getDefaultFallbackIdeas();
      selectedIdeas.push(...defaultIdeas);
    }

    return selectedIdeas.slice(0, 3);
  }

  /**
   * Get fallback validation questions
   */
  static getFallbackValidationQuestions(idea: BusinessIdea): SurveyQuestion[] {
    return [
      {
        id: 'q1',
        text: `How often do you encounter the problem that ${idea.title} solves?`,
        type: 'multiple-choice',
        options: ['Daily', 'Weekly', 'Monthly', 'Rarely', 'Never'],
        required: true
      },
      {
        id: 'q2',
        text: 'What do you currently use to solve this problem?',
        type: 'multiple-choice',
        options: ['Manual process', 'Existing software', 'Workaround', 'Nothing', 'Other'],
        required: true
      },
      {
        id: 'q3',
        text: `How likely would you be to try ${idea.title}?`,
        type: 'multiple-choice',
        options: ['Very likely', 'Somewhat likely', 'Neutral', 'Unlikely', 'Very unlikely'],
        required: true
      },
      {
        id: 'q4',
        text: 'What would you expect to pay for this solution monthly?',
        type: 'multiple-choice',
        options: ['Free', '$1-10', '$11-25', '$26-50', '$50+'],
        required: true
      }
    ];
  }

  /**
   * Get fallback task checklist
   */
  static getFallbackTaskChecklist(idea: BusinessIdea): TaskItem[] {
    return [
      {
        id: 'task1',
        title: 'Define core functionality',
        description: 'Clearly define the minimum viable features needed',
        completed: false,
        priority: 'High'
      },
      {
        id: 'task2',
        title: 'Create wireframes',
        description: 'Design basic user interface wireframes',
        completed: false,
        priority: 'High'
      },
      {
        id: 'task3',
        title: 'Set up development environment',
        description: 'Configure tools and frameworks needed',
        completed: false,
        priority: 'High'
      },
      {
        id: 'task4',
        title: 'Build core feature',
        description: 'Implement the main functionality',
        completed: false,
        priority: 'High'
      },
      {
        id: 'task5',
        title: 'Create simple landing page',
        description: 'Build a basic landing page explaining the solution',
        completed: false,
        priority: 'Medium'
      },
      {
        id: 'task6',
        title: 'Add user authentication',
        description: 'Implement basic user login/signup',
        completed: false,
        priority: 'Medium'
      },
      {
        id: 'task7',
        title: 'Test core functionality',
        description: 'Thoroughly test the main features',
        completed: false,
        priority: 'High'
      },
      {
        id: 'task8',
        title: 'Deploy MVP',
        description: 'Deploy the application to a hosting platform',
        completed: false,
        priority: 'High'
      }
    ];
  }

  /**
   * Get fallback business kit templates with complete enhancement fields
   */
  private static getFallbackKitTemplates(): Array<FallbackIdeaTemplate & { kits?: BusinessIdeaKit[] }> {
    return [
      {
        id: 'kit-template-1',
        title: 'E-commerce & Business Tools',
        problem: 'E-commerce businesses need better tools',
        solution: 'Provide specialized e-commerce solutions',
        targetAudience: 'E-commerce businesses',
        skills: ['Front-end Development', 'JavaScript', 'React', 'E-commerce'],
        interests: ['E-commerce', 'Business Tools', 'Entrepreneurship'],
        problemTypes: ['Automation', 'Productivity', 'Business Growth'],
        technicalFeasibility: 'high',
        kits: [
          {
            title: 'Product Review Aggregator',
            problem: 'E-commerce businesses struggle to collect and display customer reviews effectively across multiple platforms',
            solution: 'A widget that aggregates reviews from multiple platforms (Google, Facebook, Trustpilot) and displays them beautifully on your website',
            targetAudience: 'Small to medium e-commerce store owners',
            targetCustomer: 'Small to medium e-commerce store owners',
            validationQuestion: 'Do you currently struggle with managing customer reviews across platforms?',
            skillsRequired: ['Front-end Development', 'JavaScript', 'React'],
            technicalFeasibility: 'high',
            estimatedDevelopment: '2-3 weeks for MVP',
            revenueModel: {
              strategies: [
                {
                  type: 'Subscription',
                  description: 'Monthly subscription with tiered pricing based on review volume',
                  pricingExample: '$29/month for up to 1000 reviews, $79/month for unlimited',
                  targetSegment: 'Small e-commerce stores with 100-500 monthly orders'
                },
                {
                  type: 'Freemium',
                  description: 'Free basic widget with premium features for paid users',
                  pricingExample: 'Free for 100 reviews, $19/month for advanced features',
                  targetSegment: 'New e-commerce stores testing the market'
                }
              ],
              estimatedRevenue: '$5K-10K MRR at 200 customers'
            },
            marketAnalysis: {
              marketSize: '$500M global review management market, growing 12% annually',
              competitors: [
                {
                  name: 'Yotpo',
                  strengths: ['Established brand', 'Enterprise features', 'Large customer base'],
                  weaknesses: ['Expensive for small businesses', 'Complex setup', 'Overkill for simple needs']
                },
                {
                  name: 'Trustpilot',
                  strengths: ['Well-known platform', 'Good SEO benefits'],
                  weaknesses: ['Limited customization', 'Expensive', 'Requires customers to leave site']
                }
              ],
              opportunityGaps: [
                'Affordable solution for small businesses',
                'Simple one-click integration',
                'Beautiful customizable widgets'
              ]
            },
            resourceRequirements: {
              skills: [
                { skill: 'React/JavaScript', level: 'intermediate', canLearn: true },
                { skill: 'API Integration', level: 'intermediate', canLearn: true },
                { skill: 'UI/UX Design', level: 'beginner', canLearn: true }
              ],
              tools: [
                { name: 'Vercel/Netlify', purpose: 'Hosting and deployment', cost: 'Free tier available', alternatives: ['Railway', 'Render'] },
                { name: 'Supabase', purpose: 'Database and authentication', cost: 'Free tier available', alternatives: ['Firebase', 'PlanetScale'] },
                { name: 'Figma', purpose: 'Design and prototyping', cost: 'Free for individuals', alternatives: ['Penpot', 'Sketch'] }
              ],
              estimatedCost: {
                minimum: '$0-200',
                recommended: '$200-800',
                breakdown: [
                  { category: 'Hosting', amount: '$0-20/month' },
                  { category: 'Database', amount: '$0-25/month' },
                  { category: 'Domain', amount: '$12/year' },
                  { category: 'Tools/Services', amount: '$0-50/month' }
                ]
              }
            },
            launchGuide: {
              totalTimeline: '3-4 months to first revenue',
              phases: [
                {
                  phase: 1,
                  name: 'Validation',
                  duration: '2 weeks',
                  milestones: ['10 customer interviews completed', 'Problem validated'],
                  actions: ['Interview e-commerce store owners', 'Research competitor pricing', 'Create landing page']
                },
                {
                  phase: 2,
                  name: 'MVP Development',
                  duration: '3-4 weeks',
                  milestones: ['Core widget functional', 'Basic dashboard built'],
                  actions: ['Build review aggregation API', 'Create embeddable widget', 'Develop admin dashboard', 'Implement authentication']
                },
                {
                  phase: 3,
                  name: 'Beta Testing',
                  duration: '2-3 weeks',
                  milestones: ['5 beta customers onboarded', 'Critical bugs fixed'],
                  actions: ['Recruit beta testers', 'Gather feedback', 'Fix bugs', 'Improve UX']
                },
                {
                  phase: 4,
                  name: 'Launch Preparation',
                  duration: '2 weeks',
                  milestones: ['Payment system integrated', 'Marketing materials ready'],
                  actions: ['Set up Stripe', 'Create documentation', 'Build marketing site', 'Prepare launch content']
                },
                {
                  phase: 5,
                  name: 'Public Launch',
                  duration: '1 week',
                  milestones: ['Product launched', 'First paying customers'],
                  actions: ['Launch on Product Hunt', 'Post in e-commerce communities', 'Email beta users', 'Monitor feedback']
                }
              ]
            }
          }
        ]
      },
      {
        id: 'kit-template-2',
        title: 'Productivity & Automation Tools',
        problem: 'Knowledge workers waste time on repetitive tasks',
        solution: 'Provide automation and productivity solutions',
        targetAudience: 'Knowledge workers and professionals',
        skills: ['Automation', 'Workflow Design', 'No-Code Platforms', 'JavaScript'],
        interests: ['Productivity', 'Automation', 'Efficiency'],
        problemTypes: ['Automation', 'Productivity', 'Time Management'],
        technicalFeasibility: 'medium',
        kits: [
          {
            title: 'Simple Task Automation Tool',
            problem: 'People spend too much time on repetitive digital tasks like data entry, file organization, and report generation',
            solution: 'No-code automation tool for common workflows with pre-built templates for popular use cases',
            targetAudience: 'Knowledge workers and small business owners',
            targetCustomer: 'Knowledge workers and small business owners',
            validationQuestion: 'What repetitive tasks do you do daily that could be automated?',
            skillsRequired: ['Automation Tools', 'Workflow Design', 'No-Code Platforms'],
            technicalFeasibility: 'medium',
            estimatedDevelopment: '4-6 weeks for MVP',
            revenueModel: {
              strategies: [
                {
                  type: 'Freemium',
                  description: 'Free tier with limited automations, paid tiers for more workflows',
                  pricingExample: 'Free for 5 automations, $15/month for 50, $49/month for unlimited',
                  targetSegment: 'Individual professionals and small teams'
                },
                {
                  type: 'Usage-based',
                  description: 'Pay per automation execution',
                  pricingExample: '$0.01 per automation run, with volume discounts',
                  targetSegment: 'Businesses with variable automation needs'
                }
              ],
              estimatedRevenue: '$8K-15K MRR at 300 customers'
            },
            marketAnalysis: {
              marketSize: '$3.2B workflow automation market, growing 18% annually',
              competitors: [
                {
                  name: 'Zapier',
                  strengths: ['Huge integration library', 'Established brand', 'Reliable service'],
                  weaknesses: ['Expensive for heavy users', 'Complex for beginners', 'Steep learning curve']
                },
                {
                  name: 'Make (Integromat)',
                  strengths: ['Visual workflow builder', 'Powerful features'],
                  weaknesses: ['Overwhelming for simple tasks', 'Pricing complexity', 'Less user-friendly']
                }
              ],
              opportunityGaps: [
                'Simpler interface for non-technical users',
                'Pre-built templates for common tasks',
                'More affordable pricing for individuals'
              ]
            },
            resourceRequirements: {
              skills: [
                { skill: 'Backend Development', level: 'intermediate', canLearn: true },
                { skill: 'API Integration', level: 'intermediate', canLearn: true },
                { skill: 'Workflow Design', level: 'beginner', canLearn: true }
              ],
              tools: [
                { name: 'Node.js/Express', purpose: 'Backend server', cost: 'Free', alternatives: ['Python/Flask', 'Go'] },
                { name: 'PostgreSQL', purpose: 'Database', cost: 'Free tier on Supabase', alternatives: ['MySQL', 'MongoDB'] },
                { name: 'Bull/Redis', purpose: 'Job queue for automations', cost: '$5-10/month', alternatives: ['RabbitMQ', 'AWS SQS'] }
              ],
              estimatedCost: {
                minimum: '$100-500',
                recommended: '$500-1500',
                breakdown: [
                  { category: 'Hosting', amount: '$20-50/month' },
                  { category: 'Database', amount: '$10-30/month' },
                  { category: 'Queue Service', amount: '$5-20/month' },
                  { category: 'API Costs', amount: '$50-100/month' }
                ]
              }
            },
            launchGuide: {
              totalTimeline: '4-6 months to first revenue',
              phases: [
                {
                  phase: 1,
                  name: 'Research & Validation',
                  duration: '2-3 weeks',
                  milestones: ['20 user interviews completed', 'Top 5 automation needs identified'],
                  actions: ['Survey target users', 'Analyze competitor features', 'Identify most common workflows', 'Validate pricing']
                },
                {
                  phase: 2,
                  name: 'Core Platform Development',
                  duration: '4-5 weeks',
                  milestones: ['Workflow engine built', 'Basic UI functional'],
                  actions: ['Build automation engine', 'Create workflow builder UI', 'Implement user authentication', 'Set up database']
                },
                {
                  phase: 3,
                  name: 'Integration Development',
                  duration: '3-4 weeks',
                  milestones: ['5 key integrations working', 'Templates created'],
                  actions: ['Build top 5 integrations', 'Create workflow templates', 'Test automation reliability', 'Add error handling']
                },
                {
                  phase: 4,
                  name: 'Beta Testing',
                  duration: '3 weeks',
                  milestones: ['10 beta users active', 'Core bugs resolved'],
                  actions: ['Recruit beta testers', 'Monitor automation success rates', 'Gather feedback', 'Iterate on UX']
                },
                {
                  phase: 5,
                  name: 'Payment & Polish',
                  duration: '2 weeks',
                  milestones: ['Payment system live', 'Documentation complete'],
                  actions: ['Integrate Stripe', 'Write user guides', 'Create video tutorials', 'Polish UI']
                },
                {
                  phase: 6,
                  name: 'Launch',
                  duration: '1-2 weeks',
                  milestones: ['Public launch completed', 'First 20 paying customers'],
                  actions: ['Launch on Product Hunt', 'Post in productivity communities', 'Run initial ads', 'Monitor metrics']
                }
              ]
            }
          }
        ]
      },
      {
        id: 'kit-template-3',
        title: 'Data & Analytics Tools',
        problem: 'Businesses need better data insights',
        solution: 'Provide data analysis and monitoring solutions',
        targetAudience: 'Data-driven businesses',
        skills: ['Back-end Development', 'APIs', 'Database', 'Data Analysis'],
        interests: ['Data Analysis', 'Business Intelligence', 'Analytics'],
        problemTypes: ['Data Processing', 'Analytics', 'Monitoring'],
        technicalFeasibility: 'medium',
        kits: [
          {
            title: 'Website Performance Monitor',
            problem: 'Website owners don\'t know when their site is slow or down, leading to lost customers and revenue',
            solution: 'Automated monitoring with instant alerts and performance insights, tracking uptime, speed, and user experience metrics',
            targetAudience: 'Website owners and developers',
            targetCustomer: 'Website owners and developers',
            validationQuestion: 'Have you ever lost customers due to website downtime?',
            skillsRequired: ['Back-end Development', 'APIs', 'Database'],
            technicalFeasibility: 'medium',
            estimatedDevelopment: '3-4 weeks for MVP',
            revenueModel: {
              strategies: [
                {
                  type: 'Tiered Subscription',
                  description: 'Monthly plans based on number of websites and check frequency',
                  pricingExample: '$9/month for 1 site (5-min checks), $29/month for 5 sites (1-min checks)',
                  targetSegment: 'Small business owners and freelance developers'
                },
                {
                  type: 'Usage-based',
                  description: 'Pay per monitored endpoint',
                  pricingExample: '$2/month per monitored URL',
                  targetSegment: 'Agencies managing multiple client sites'
                }
              ],
              estimatedRevenue: '$6K-12K MRR at 250 customers'
            },
            marketAnalysis: {
              marketSize: '$1.8B website monitoring market, growing 14% annually',
              competitors: [
                {
                  name: 'Pingdom',
                  strengths: ['Established service', 'Comprehensive features', 'Global monitoring'],
                  weaknesses: ['Expensive for small sites', 'Complex interface', 'Overkill for simple needs']
                },
                {
                  name: 'UptimeRobot',
                  strengths: ['Free tier available', 'Simple setup'],
                  weaknesses: ['Limited performance insights', 'Basic alerting', 'Dated interface']
                }
              ],
              opportunityGaps: [
                'Better performance insights for non-technical users',
                'More affordable pricing for small sites',
                'Modern, intuitive dashboard'
              ]
            },
            resourceRequirements: {
              skills: [
                { skill: 'Backend Development', level: 'intermediate', canLearn: true },
                { skill: 'Monitoring/DevOps', level: 'intermediate', canLearn: true },
                { skill: 'Data Visualization', level: 'beginner', canLearn: true }
              ],
              tools: [
                { name: 'Node.js', purpose: 'Monitoring service', cost: 'Free', alternatives: ['Python', 'Go'] },
                { name: 'TimescaleDB', purpose: 'Time-series data storage', cost: 'Free tier available', alternatives: ['InfluxDB', 'PostgreSQL'] },
                { name: 'Chart.js', purpose: 'Performance graphs', cost: 'Free', alternatives: ['Recharts', 'D3.js'] },
                { name: 'Twilio', purpose: 'SMS alerts', cost: '$0.0075 per SMS', alternatives: ['AWS SNS', 'Vonage'] }
              ],
              estimatedCost: {
                minimum: '$50-300',
                recommended: '$300-1000',
                breakdown: [
                  { category: 'Hosting', amount: '$15-40/month' },
                  { category: 'Database', amount: '$10-30/month' },
                  { category: 'Monitoring Infrastructure', amount: '$20-50/month' },
                  { category: 'SMS/Email Alerts', amount: '$5-20/month' }
                ]
              }
            },
            launchGuide: {
              totalTimeline: '3-5 months to first revenue',
              phases: [
                {
                  phase: 1,
                  name: 'Validation',
                  duration: '1-2 weeks',
                  milestones: ['15 potential customers interviewed', 'Key features prioritized'],
                  actions: ['Interview website owners', 'Research competitor gaps', 'Define MVP features', 'Create landing page']
                },
                {
                  phase: 2,
                  name: 'Core Monitoring Engine',
                  duration: '3 weeks',
                  milestones: ['Monitoring service functional', 'Basic alerts working'],
                  actions: ['Build monitoring service', 'Implement uptime checks', 'Set up alert system', 'Create database schema']
                },
                {
                  phase: 3,
                  name: 'Dashboard Development',
                  duration: '2-3 weeks',
                  milestones: ['Dashboard UI complete', 'Real-time updates working'],
                  actions: ['Build dashboard UI', 'Create performance graphs', 'Implement real-time updates', 'Add user authentication']
                },
                {
                  phase: 4,
                  name: 'Beta Testing',
                  duration: '2-3 weeks',
                  milestones: ['10 sites being monitored', 'Alert reliability confirmed'],
                  actions: ['Recruit beta testers', 'Monitor service reliability', 'Fix bugs', 'Optimize performance']
                },
                {
                  phase: 5,
                  name: 'Payment Integration',
                  duration: '1-2 weeks',
                  milestones: ['Billing system live', 'Subscription tiers configured'],
                  actions: ['Integrate Stripe', 'Set up subscription plans', 'Create billing dashboard', 'Test payment flows']
                },
                {
                  phase: 6,
                  name: 'Launch',
                  duration: '1 week',
                  milestones: ['Public launch', 'First 25 paying customers'],
                  actions: ['Launch on Product Hunt', 'Post in developer communities', 'Reach out to beta users', 'Start content marketing']
                }
              ]
            }
          }
        ]
      }
    ];
  }

  /**
   * Get default fallback business kits when no templates match
   */
  private static getDefaultFallbackBusinessKits(): BusinessIdeaKit[] {
    const allFallbackKits: BusinessIdeaKit[] = [
      {
        title: 'Micro-Learning Platform',
        problem: 'Professionals struggle to find time for skill development in their busy schedules',
        solution: '5-minute daily lessons delivered via email or app, focusing on practical skills',
        targetAudience: 'Busy professionals wanting to upskill',
        targetCustomer: 'Busy professionals wanting to upskill',
        validationQuestion: 'Would you pay for bite-sized daily learning content?',
        skillsRequired: ['Content Design', 'Front-end Development', 'Email Automation'],
        technicalFeasibility: 'high',
        estimatedDevelopment: '2-3 weeks for MVP',
        revenueModel: {
          strategies: [
            {
              type: 'Subscription',
              description: 'Monthly or annual subscription for unlimited access',
              pricingExample: '$12/month or $99/year',
              targetSegment: 'Individual professionals'
            },
            {
              type: 'Freemium',
              description: 'Free basic lessons with premium content for subscribers',
              pricingExample: 'Free for 3 lessons/week, $12/month for daily lessons',
              targetSegment: 'Users wanting to try before committing'
            }
          ],
          estimatedRevenue: '$10K-20K MRR at 1000 subscribers'
        },
        marketAnalysis: {
          marketSize: '$7.5B microlearning market, growing 13% annually',
          competitors: [
            {
              name: 'Blinkist',
              strengths: ['Large content library', 'Well-known brand', 'Good app experience'],
              weaknesses: ['Book summaries only', 'Expensive', 'Not skill-focused']
            },
            {
              name: 'Duolingo',
              strengths: ['Gamification', 'Free tier', 'Habit-forming'],
              weaknesses: ['Language learning only', 'Not professional skills', 'Time-consuming']
            }
          ],
          opportunityGaps: [
            'Focus on professional skills',
            'Truly 5-minute lessons',
            'Practical, actionable content'
          ]
        },
        resourceRequirements: {
          skills: [
            { skill: 'Content Creation', level: 'intermediate', canLearn: true },
            { skill: 'Web Development', level: 'beginner', canLearn: true },
            { skill: 'Email Marketing', level: 'beginner', canLearn: true }
          ],
          tools: [
            { name: 'Next.js', purpose: 'Web application', cost: 'Free', alternatives: ['React', 'Vue'] },
            { name: 'SendGrid', purpose: 'Email delivery', cost: 'Free for 100 emails/day', alternatives: ['Mailgun', 'AWS SES'] },
            { name: 'Notion', purpose: 'Content management', cost: 'Free tier available', alternatives: ['Airtable', 'Google Sheets'] }
          ],
          estimatedCost: {
            minimum: '$0-200',
            recommended: '$200-600',
            breakdown: [
              { category: 'Hosting', amount: '$0-20/month' },
              { category: 'Email Service', amount: '$0-30/month' },
              { category: 'Domain', amount: '$12/year' },
              { category: 'Content Tools', amount: '$0-20/month' }
            ]
          }
        },
        launchGuide: {
          totalTimeline: '2-4 months to first revenue',
          phases: [
            {
              phase: 1,
              name: 'Content Planning',
              duration: '1-2 weeks',
              milestones: ['First 30 lessons outlined', 'Content format defined'],
              actions: ['Research popular skills', 'Create lesson templates', 'Write first 10 lessons', 'Test lesson length']
            },
            {
              phase: 2,
              name: 'Platform Development',
              duration: '2-3 weeks',
              milestones: ['Website live', 'Email system working'],
              actions: ['Build landing page', 'Set up email automation', 'Create user dashboard', 'Implement authentication']
            },
            {
              phase: 3,
              name: 'Content Creation',
              duration: '3-4 weeks',
              milestones: ['30 lessons ready', 'Content calendar set'],
              actions: ['Write remaining lessons', 'Create visuals', 'Record audio (if applicable)', 'Set up content schedule']
            },
            {
              phase: 4,
              name: 'Beta Launch',
              duration: '2 weeks',
              milestones: ['50 beta users', 'Feedback collected'],
              actions: ['Launch to email list', 'Gather feedback', 'Refine content', 'Fix bugs']
            },
            {
              phase: 5,
              name: 'Payment Setup',
              duration: '1 week',
              milestones: ['Payment system live', 'Pricing finalized'],
              actions: ['Integrate Stripe', 'Set up subscription plans', 'Create pricing page', 'Test checkout flow']
            },
            {
              phase: 6,
              name: 'Public Launch',
              duration: '1 week',
              milestones: ['Public launch', 'First 100 subscribers'],
              actions: ['Launch on Product Hunt', 'Post on social media', 'Reach out to influencers', 'Start content marketing']
            }
          ]
        }
      },
      {
        title: 'Meeting Cost Calculator',
        problem: 'Companies waste money on ineffective meetings with too many attendees and unclear agendas',
        solution: 'Real-time calculator showing the cost of current meeting based on attendee salaries and time',
        targetAudience: 'Team leads and project managers',
        targetCustomer: 'Team leads and project managers',
        validationQuestion: 'Do you think your team has too many expensive meetings?',
        skillsRequired: ['Front-end Development', 'Productivity Analytics'],
        technicalFeasibility: 'high',
        estimatedDevelopment: '1-2 weeks for MVP',
        revenueModel: {
          strategies: [
            {
              type: 'Freemium',
              description: 'Free basic calculator with premium team features',
              pricingExample: 'Free for individuals, $5/user/month for teams',
              targetSegment: 'Individual managers and small teams'
            },
            {
              type: 'One-time Purchase',
              description: 'Lifetime access with one-time payment',
              pricingExample: '$49 one-time for unlimited use',
              targetSegment: 'Companies wanting simple pricing'
            }
          ],
          estimatedRevenue: '$3K-8K MRR at 500 users'
        },
        marketAnalysis: {
          marketSize: '$4.5B meeting productivity market, growing 10% annually',
          competitors: [
            {
              name: 'Fellow.app',
              strengths: ['Comprehensive meeting management', 'Good integrations'],
              weaknesses: ['Expensive', 'Complex for simple needs', 'Requires full adoption']
            },
            {
              name: 'Clockwise',
              strengths: ['AI-powered scheduling', 'Calendar optimization'],
              weaknesses: ['Doesn\'t show meeting costs', 'Focus on scheduling not cost', 'Expensive']
            }
          ],
          opportunityGaps: [
            'Simple cost visualization',
            'No complex setup required',
            'Affordable for small teams'
          ]
        },
        resourceRequirements: {
          skills: [
            { skill: 'JavaScript/React', level: 'beginner', canLearn: true },
            { skill: 'UI Design', level: 'beginner', canLearn: true }
          ],
          tools: [
            { name: 'React', purpose: 'Frontend framework', cost: 'Free', alternatives: ['Vue', 'Svelte'] },
            { name: 'Vercel', purpose: 'Hosting', cost: 'Free tier available', alternatives: ['Netlify', 'GitHub Pages'] },
            { name: 'Tailwind CSS', purpose: 'Styling', cost: 'Free', alternatives: ['Bootstrap', 'Material-UI'] }
          ],
          estimatedCost: {
            minimum: '$0-50',
            recommended: '$50-200',
            breakdown: [
              { category: 'Hosting', amount: '$0/month' },
              { category: 'Domain', amount: '$12/year' },
              { category: 'Analytics', amount: '$0-10/month' }
            ]
          }
        },
        launchGuide: {
          totalTimeline: '1-2 months to first revenue',
          phases: [
            {
              phase: 1,
              name: 'Validation',
              duration: '3-5 days',
              milestones: ['Problem validated', 'Pricing research done'],
              actions: ['Survey managers', 'Research meeting costs', 'Analyze competitors', 'Create mockups']
            },
            {
              phase: 2,
              name: 'MVP Development',
              duration: '1-2 weeks',
              milestones: ['Calculator functional', 'Basic UI complete'],
              actions: ['Build calculator logic', 'Create UI', 'Add export feature', 'Test calculations']
            },
            {
              phase: 3,
              name: 'Beta Testing',
              duration: '1 week',
              milestones: ['20 beta users', 'Feedback incorporated'],
              actions: ['Share with managers', 'Gather feedback', 'Refine UX', 'Add requested features']
            },
            {
              phase: 4,
              name: 'Payment Integration',
              duration: '3-5 days',
              milestones: ['Payment working', 'Team features ready'],
              actions: ['Integrate Stripe', 'Build team dashboard', 'Add user management', 'Test payments']
            },
            {
              phase: 5,
              name: 'Launch',
              duration: '1 week',
              milestones: ['Public launch', 'First 50 users'],
              actions: ['Launch on Product Hunt', 'Post on LinkedIn', 'Share in Slack communities', 'Write blog post']
            }
          ]
        }
      },
      {
        title: 'Email Template Library',
        problem: 'Professionals rewrite the same types of emails repeatedly, wasting time and mental energy',
        solution: 'Smart template system with customizable professional email templates for common scenarios',
        targetAudience: 'Sales teams, customer service, and business professionals',
        targetCustomer: 'Sales teams, customer service, and business professionals',
        validationQuestion: 'How much time do you spend writing similar emails?',
        skillsRequired: ['Content Strategy', 'Front-end Development'],
        technicalFeasibility: 'high',
        estimatedDevelopment: '2-3 weeks for MVP',
        revenueModel: {
          strategies: [
            {
              type: 'Freemium',
              description: 'Free basic templates with premium templates for paid users',
              pricingExample: 'Free for 10 templates, $9/month for unlimited',
              targetSegment: 'Individual professionals'
            },
            {
              type: 'Team Subscription',
              description: 'Team plans with shared template libraries',
              pricingExample: '$29/month for teams up to 10 users',
              targetSegment: 'Small teams and departments'
            }
          ],
          estimatedRevenue: '$4K-10K MRR at 400 users'
        },
        marketAnalysis: {
          marketSize: '$2.1B email productivity market, growing 11% annually',
          competitors: [
            {
              name: 'Grammarly',
              strengths: ['Well-known brand', 'Good writing assistance'],
              weaknesses: ['Not template-focused', 'Expensive premium tier', 'Limited customization']
            },
            {
              name: 'TextExpander',
              strengths: ['Powerful snippet system', 'Cross-platform'],
              weaknesses: ['Complex for simple needs', 'Expensive', 'Steep learning curve']
            }
          ],
          opportunityGaps: [
            'Email-specific templates',
            'Industry-specific content',
            'Simple, affordable solution'
          ]
        },
        resourceRequirements: {
          skills: [
            { skill: 'Content Writing', level: 'intermediate', canLearn: true },
            { skill: 'Frontend Development', level: 'beginner', canLearn: true },
            { skill: 'UX Design', level: 'beginner', canLearn: true }
          ],
          tools: [
            { name: 'React', purpose: 'Web application', cost: 'Free', alternatives: ['Vue', 'Svelte'] },
            { name: 'Supabase', purpose: 'Database and auth', cost: 'Free tier available', alternatives: ['Firebase', 'MongoDB'] },
            { name: 'Vercel', purpose: 'Hosting', cost: 'Free tier available', alternatives: ['Netlify', 'Railway'] }
          ],
          estimatedCost: {
            minimum: '$0-100',
            recommended: '$100-400',
            breakdown: [
              { category: 'Hosting', amount: '$0-15/month' },
              { category: 'Database', amount: '$0-20/month' },
              { category: 'Domain', amount: '$12/year' },
              { category: 'Tools', amount: '$0-10/month' }
            ]
          }
        },
        launchGuide: {
          totalTimeline: '2-3 months to first revenue',
          phases: [
            {
              phase: 1,
              name: 'Content Research',
              duration: '1-2 weeks',
              milestones: ['50 template ideas collected', 'Categories defined'],
              actions: ['Survey professionals', 'Analyze common email types', 'Define template categories', 'Create content plan']
            },
            {
              phase: 2,
              name: 'Template Creation',
              duration: '2-3 weeks',
              milestones: ['30 templates written', 'Quality reviewed'],
              actions: ['Write templates', 'Get feedback', 'Refine content', 'Add customization options']
            },
            {
              phase: 3,
              name: 'Platform Development',
              duration: '2-3 weeks',
              milestones: ['Template browser built', 'Copy functionality working'],
              actions: ['Build template UI', 'Add search/filter', 'Implement copy feature', 'Add user accounts']
            },
            {
              phase: 4,
              name: 'Beta Testing',
              duration: '1-2 weeks',
              milestones: ['30 beta users', 'Templates validated'],
              actions: ['Recruit testers', 'Track template usage', 'Gather feedback', 'Add requested templates']
            },
            {
              phase: 5,
              name: 'Monetization',
              duration: '1 week',
              milestones: ['Payment system live', 'Premium templates ready'],
              actions: ['Integrate Stripe', 'Create premium templates', 'Set up subscription', 'Test payment flow']
            },
            {
              phase: 6,
              name: 'Launch',
              duration: '1 week',
              milestones: ['Public launch', 'First 100 users'],
              actions: ['Launch on Product Hunt', 'Post in professional communities', 'Email marketing', 'Content marketing']
            }
          ]
        }
      }
    ];

    const shuffled = [...allFallbackKits].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }

  /**
   * Get fallback idea templates (legacy method for backward compatibility)
   */
  private static getFallbackTemplates(): FallbackIdeaTemplate[] {
    return [
      {
        id: 'template-1',
        title: 'E-commerce Tools',
        problem: 'E-commerce businesses need better tools',
        solution: 'Provide specialized e-commerce solutions',
        targetAudience: 'E-commerce businesses',
        skills: ['Front-end Development', 'JavaScript', 'React'],
        interests: ['E-commerce', 'Business Tools'],
        problemTypes: ['Automation', 'Productivity'],
        technicalFeasibility: 'high',
        ideas: [
          {
            title: 'Product Review Aggregator',
            problem: 'E-commerce businesses struggle to collect and display customer reviews effectively',
            solution: 'A widget that aggregates reviews from multiple platforms and displays them beautifully',
            targetAudience: 'Small to medium e-commerce store owners',
            targetCustomer: 'Small to medium e-commerce store owners',
            validationQuestion: 'Do you currently struggle with managing customer reviews across platforms?',
            skillsRequired: ['Front-end Development', 'JavaScript', 'React'],
            technicalFeasibility: 'high',
            estimatedDevelopment: '2-3 weeks'
          },
          {
            title: 'Social Media Scheduler for Small Business',
            problem: 'Small businesses waste time manually posting to social media',
            solution: 'Simple scheduling tool with templates for common business posts',
            targetAudience: 'Local businesses and solopreneurs',
            targetCustomer: 'Local businesses and solopreneurs',
            validationQuestion: 'How much time do you spend on social media posting weekly?',
            skillsRequired: ['Front-end Development', 'JavaScript', 'React'],
            technicalFeasibility: 'high',
            estimatedDevelopment: '3-4 weeks'
          }
        ]
      },
      {
        id: 'template-2',
        title: 'Data & Analytics Tools',
        problem: 'Businesses need better data insights',
        solution: 'Provide data analysis and monitoring solutions',
        targetAudience: 'Data-driven businesses',
        skills: ['Back-end Development', 'APIs', 'Database'],
        interests: ['Data Analysis', 'Business Intelligence'],
        problemTypes: ['Data Processing', 'Analytics'],
        technicalFeasibility: 'medium',
        ideas: [
          {
            title: 'Website Performance Monitor',
            problem: 'Website owners don\'t know when their site is slow or down',
            solution: 'Automated monitoring with instant alerts and performance insights',
            targetAudience: 'Website owners and developers',
            targetCustomer: 'Website owners and developers',
            validationQuestion: 'Have you ever lost customers due to website downtime?',
            skillsRequired: ['Back-end Development', 'APIs', 'Database'],
            technicalFeasibility: 'medium',
            estimatedDevelopment: '3-4 weeks'
          }
        ]
      }
    ];
  }

  /**
   * Get randomized fallback ideas when AI API fails (legacy method for backward compatibility)
   */
  private static getDefaultFallbackIdeas(): BusinessIdea[] {
    const allFallbackIdeas = [
      {
        title: 'Simple Task Automation Tool',
        problem: 'People spend too much time on repetitive digital tasks',
        solution: 'No-code automation tool for common workflows',
        targetAudience: 'Knowledge workers and small business owners',
        targetCustomer: 'Knowledge workers and small business owners',
        validationQuestion: 'What repetitive tasks do you do daily that could be automated?',
        skillsRequired: ['Automation Tools', 'Workflow Design', 'No-Code Platforms'],
        technicalFeasibility: 'medium' as const,
        estimatedDevelopment: '4-6 weeks'
      },
      {
        title: 'Micro-Learning Platform',
        problem: 'Professionals struggle to find time for skill development',
        solution: '5-minute daily lessons',
        targetAudience: 'Busy professionals wanting to upskill',
        targetCustomer: 'Busy professionals wanting to upskill',
        validationQuestion: 'Would you pay for bite-sized daily learning content?',
        skillsRequired: ['Content Design', 'Front-end Development', 'Email Automation'],
        technicalFeasibility: 'high' as const,
        estimatedDevelopment: '2-3 weeks'
      },
      {
        title: 'Local Service Finder',
        problem: 'Finding reliable local services is time-consuming and uncertain',
        solution: 'Curated directory with verified reviews and instant booking',
        targetAudience: 'Homeowners and renters needing local services',
        targetCustomer: 'Homeowners and renters needing local services',
        validationQuestion: 'How do you currently find local service providers?',
        skillsRequired: ['Backend Development', 'Database Design', 'Local SEO'],
        technicalFeasibility: 'medium' as const,
        estimatedDevelopment: '4-5 weeks'
      },
      {
        title: 'Password Health Monitor',
        problem: 'People reuse weak passwords across multiple accounts',
        solution: 'Security tool that monitors password strength and breach exposure',
        targetAudience: 'Security-conscious individuals and small teams',
        targetCustomer: 'Security-conscious individuals and small teams',
        validationQuestion: 'How often do you worry about your password security?',
        skillsRequired: ['Cybersecurity', 'Backend Development', 'API Integration'],
        technicalFeasibility: 'high' as const,
        estimatedDevelopment: '3-4 weeks'
      },
      {
        title: 'Meeting Cost Calculator',
        problem: 'Companies waste money on ineffective meetings with too many attendees',
        solution: 'Real-time calculator showing the cost of current meeting',
        targetAudience: 'Team leads and project managers',
        targetCustomer: 'Team leads and project managers',
        validationQuestion: 'Do you think your team has too many expensive meetings?',
        skillsRequired: ['Front-end Development', 'Productivity Analytics'],
        technicalFeasibility: 'high' as const,
        estimatedDevelopment: '1-2 weeks'
      },
      {
        title: 'Habit Streak Tracker',
        problem: 'People struggle to maintain consistent habits and routines',
        solution: 'Simple visual tracker with social accountability features',
        targetAudience: 'Self-improvement enthusiasts and professionals',
        targetCustomer: 'Self-improvement enthusiasts and professionals',
        validationQuestion: 'What habits do you wish you could stick to consistently?',
        skillsRequired: ['Mobile Development', 'Behavioral Design'],
        technicalFeasibility: 'high' as const,
        estimatedDevelopment: '2-3 weeks'
      },
      {
        title: 'Email Template Library',
        problem: 'Professionals rewrite the same types of emails repeatedly',
        solution: 'Smart template system with customizable professional email templates',
        targetAudience: 'Sales teams, customer service, and business professionals',
        targetCustomer: 'Sales teams, customer service, and business professionals',
        validationQuestion: 'How much time do you spend writing similar emails?',
        skillsRequired: ['Content Strategy', 'Front-end Development'],
        technicalFeasibility: 'high' as const,
        estimatedDevelopment: '2-3 weeks'
      },
      {
        title: 'Focus Session Timer',
        problem: 'Remote workers struggle with distractions and time management',
        solution: 'Pomodoro timer with website blocking and productivity insights',
        targetAudience: 'Remote workers and freelancers',
        targetCustomer: 'Remote workers and freelancers',
        validationQuestion: 'What distracts you most during focused work sessions?',
        skillsRequired: ['Desktop Development', 'Productivity Coaching'],
        technicalFeasibility: 'medium' as const,
        estimatedDevelopment: '3-4 weeks'
      },
      {
        title: 'Newsletter Summarizer',
        problem: 'People subscribe to many newsletters but don\'t have time to read them all',
        solution: 'AI-powered tool that creates weekly summaries of newsletter content',
        targetAudience: 'Information workers and knowledge enthusiasts',
        targetCustomer: 'Information workers and knowledge enthusiasts',
        validationQuestion: 'How many unread newsletters are in your inbox right now?',
        skillsRequired: ['Natural Language Processing', 'Backend Development'],
        technicalFeasibility: 'medium' as const,
        estimatedDevelopment: '4-5 weeks'
      }
    ];

    const shuffled = [...allFallbackIdeas].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }

  /**
   * Get fallback business kit templates in Chinese
   */
  private static getFallbackKitTemplatesZh(): Array<FallbackIdeaTemplate & { kits?: BusinessIdeaKit[] }> {
    return [
      {
        id: 'kit-template-zh-1',
        title: '电商与商业工具',
        problem: '电商企业需要更好的工具',
        solution: '提供专业的电商解决方案',
        targetAudience: '电商企业',
        skills: ['前端开发', 'JavaScript', 'React', '电子商务'],
        interests: ['电子商务', '商业工具', '创业'],
        problemTypes: ['自动化', '生产力', '业务增长'],
        technicalFeasibility: 'high',
        kits: [
          {
            title: '产品评论聚合器',
            problem: '电商企业难以有效地收集和展示来自多个平台的客户评论',
            solution: '一个小部件，可以聚合来自多个平台（Google、Facebook、Trustpilot）的评论，并在您的网站上精美地展示它们',
            targetAudience: '中小型电商店主',
            targetCustomer: '中小型电商店主',
            validationQuestion: '您目前是否在跨平台管理客户评论方面遇到困难？',
            skillsRequired: ['前端开发', 'JavaScript', 'React'],
            technicalFeasibility: 'high',
            estimatedDevelopment: '2-3周完成MVP',
            revenueModel: {
              strategies: [
                {
                  type: '订阅制',
                  description: '基于评论数量的分层定价月度订阅',
                  pricingExample: '1000条评论以内每月29美元，无限评论每月79美元',
                  targetSegment: '月订单量100-500的小型电商店铺'
                },
                {
                  type: '免费增值',
                  description: '免费基础小部件，付费用户享有高级功能',
                  pricingExample: '100条评论免费，高级功能每月19美元',
                  targetSegment: '测试市场的新电商店铺'
                }
              ],
              estimatedRevenue: '200个客户时月经常性收入5千-1万美元'
            },
            marketAnalysis: {
              marketSize: '全球评论管理市场规模5亿美元，年增长12%',
              competitors: [
                {
                  name: 'Yotpo',
                  strengths: ['知名品牌', '企业级功能', '庞大的客户群'],
                  weaknesses: ['对小企业来说太贵', '设置复杂', '对简单需求来说过于复杂']
                },
                {
                  name: 'Trustpilot',
                  strengths: ['知名平台', '良好的SEO效益'],
                  weaknesses: ['定制化有限', '价格昂贵', '需要客户离开网站']
                }
              ],
              opportunityGaps: [
                '为小企业提供经济实惠的解决方案',
                '简单的一键集成',
                '美观可定制的小部件'
              ]
            },
            resourceRequirements: {
              skills: [
                { skill: 'React/JavaScript', level: 'intermediate', canLearn: true },
                { skill: 'API集成', level: 'intermediate', canLearn: true },
                { skill: 'UI/UX设计', level: 'beginner', canLearn: true }
              ],
              tools: [
                { name: 'Vercel/Netlify', purpose: '托管和部署', cost: '有免费套餐', alternatives: ['Railway', 'Render'] },
                { name: 'Supabase', purpose: '数据库和身份验证', cost: '有免费套餐', alternatives: ['Firebase', 'PlanetScale'] },
                { name: 'Figma', purpose: '设计和原型制作', cost: '个人用户免费', alternatives: ['Penpot', 'Sketch'] }
              ],
              estimatedCost: {
                minimum: '$0-200',
                recommended: '$200-800',
                breakdown: [
                  { category: '托管', amount: '$0-20/月' },
                  { category: '数据库', amount: '$0-25/月' },
                  { category: '域名', amount: '$12/年' },
                  { category: '工具/服务', amount: '$0-50/月' }
                ]
              }
            },
            launchGuide: {
              totalTimeline: '3-4个月获得首笔收入',
              phases: [
                {
                  phase: 1,
                  name: '验证',
                  duration: '2周',
                  milestones: ['完成10次客户访谈', '问题得到验证'],
                  actions: ['访谈电商店主', '研究竞争对手定价', '创建落地页']
                },
                {
                  phase: 2,
                  name: 'MVP开发',
                  duration: '3-4周',
                  milestones: ['核心小部件功能完成', '基础仪表板构建完成'],
                  actions: ['构建评论聚合API', '创建可嵌入小部件', '开发管理仪表板', '实现身份验证']
                },
                {
                  phase: 3,
                  name: 'Beta测试',
                  duration: '2-3周',
                  milestones: ['5个Beta客户上线', '关键错误已修复'],
                  actions: ['招募Beta测试者', '收集反馈', '修复错误', '改进用户体验']
                },
                {
                  phase: 4,
                  name: '发布准备',
                  duration: '2周',
                  milestones: ['支付系统集成完成', '营销材料准备就绪'],
                  actions: ['设置Stripe', '创建文档', '构建营销网站', '准备发布内容']
                },
                {
                  phase: 5,
                  name: '公开发布',
                  duration: '1周',
                  milestones: ['产品发布', '首批付费客户'],
                  actions: ['在Product Hunt上发布', '在电商社区发帖', '给Beta用户发邮件', '监控反馈']
                }
              ]
            }
          }
        ]
      }
    ];
  }

  /**
   * Get default fallback business kits in Chinese
   */
  private static getDefaultFallbackBusinessKitsZh(): BusinessIdeaKit[] {
    const allFallbackKits: BusinessIdeaKit[] = [
      {
        title: '微学习平台',
        problem: '专业人士在繁忙的日程中难以找到时间进行技能发展',
        solution: '通过电子邮件或应用程序提供每日5分钟课程，专注于实用技能',
        targetAudience: '希望提升技能的忙碌专业人士',
        targetCustomer: '希望提升技能的忙碌专业人士',
        validationQuestion: '您愿意为每日精简学习内容付费吗？',
        skillsRequired: ['内容设计', '前端开发', '电子邮件自动化'],
        technicalFeasibility: 'high',
        estimatedDevelopment: '2-3周完成MVP',
        revenueModel: {
          strategies: [
            {
              type: '订阅制',
              description: '月度或年度订阅，无限访问',
              pricingExample: '每月12美元或每年99美元',
              targetSegment: '个人专业人士'
            },
            {
              type: '免费增值',
              description: '免费基础课程，订阅者享有高级内容',
              pricingExample: '每周3节课免费，每日课程每月12美元',
              targetSegment: '想要先试用再承诺的用户'
            }
          ],
          estimatedRevenue: '1000个订阅者时月经常性收入1万-2万美元'
        },
        marketAnalysis: {
          marketSize: '微学习市场规模75亿美元，年增长13%',
          competitors: [
            {
              name: 'Blinkist',
              strengths: ['庞大的内容库', '知名品牌', '良好的应用体验'],
              weaknesses: ['仅限图书摘要', '价格昂贵', '不专注于技能']
            },
            {
              name: 'Duolingo',
              strengths: ['游戏化', '免费套餐', '养成习惯'],
              weaknesses: ['仅限语言学习', '不是专业技能', '耗时']
            }
          ],
          opportunityGaps: [
            '专注于专业技能',
            '真正的5分钟课程',
            '实用、可操作的内容'
          ]
        },
        resourceRequirements: {
          skills: [
            { skill: '内容创作', level: 'intermediate', canLearn: true },
            { skill: 'Web开发', level: 'beginner', canLearn: true },
            { skill: '电子邮件营销', level: 'beginner', canLearn: true }
          ],
          tools: [
            { name: 'Next.js', purpose: 'Web应用程序', cost: '免费', alternatives: ['React', 'Vue'] },
            { name: 'SendGrid', purpose: '电子邮件发送', cost: '每天100封邮件免费', alternatives: ['Mailgun', 'AWS SES'] },
            { name: 'Notion', purpose: '内容管理', cost: '有免费套餐', alternatives: ['Airtable', 'Google Sheets'] }
          ],
          estimatedCost: {
            minimum: '$0-200',
            recommended: '$200-600',
            breakdown: [
              { category: '托管', amount: '$0-20/月' },
              { category: '电子邮件服务', amount: '$0-30/月' },
              { category: '域名', amount: '$12/年' },
              { category: '内容工具', amount: '$0-20/月' }
            ]
          }
        },
        launchGuide: {
          totalTimeline: '2-4个月获得首笔收入',
          phases: [
            {
              phase: 1,
              name: '内容规划',
              duration: '1-2周',
              milestones: ['前30节课大纲完成', '内容格式确定'],
              actions: ['研究热门技能', '创建课程模板', '编写前10节课', '测试课程长度']
            },
            {
              phase: 2,
              name: '平台开发',
              duration: '2-3周',
              milestones: ['网站上线', '电子邮件系统运行'],
              actions: ['构建落地页', '设置电子邮件自动化', '创建用户仪表板', '实现身份验证']
            },
            {
              phase: 3,
              name: '内容创作',
              duration: '3-4周',
              milestones: ['30节课准备就绪', '内容日历设置完成'],
              actions: ['编写剩余课程', '创建视觉效果', '录制音频（如适用）', '设置内容计划']
            },
            {
              phase: 4,
              name: 'Beta发布',
              duration: '2周',
              milestones: ['50个Beta用户', '收集反馈'],
              actions: ['向邮件列表发布', '收集反馈', '完善内容', '修复错误']
            },
            {
              phase: 5,
              name: '支付设置',
              duration: '1周',
              milestones: ['支付系统上线', '定价最终确定'],
              actions: ['集成Stripe', '设置订阅计划', '创建定价页面', '测试结账流程']
            },
            {
              phase: 6,
              name: '公开发布',
              duration: '1周',
              milestones: ['公开发布', '首批100个订阅者'],
              actions: ['在Product Hunt上发布', '在社交媒体发帖', '联系影响者', '开始内容营销']
            }
          ]
        }
      },
      {
        title: '会议成本计算器',
        problem: '公司在参与人数过多、议程不明确的低效会议上浪费金钱',
        solution: '基于与会者工资和时间显示当前会议成本的实时计算器',
        targetAudience: '团队负责人和项目经理',
        targetCustomer: '团队负责人和项目经理',
        validationQuestion: '您认为您的团队有太多昂贵的会议吗？',
        skillsRequired: ['前端开发', '生产力分析'],
        technicalFeasibility: 'high',
        estimatedDevelopment: '1-2周完成MVP',
        revenueModel: {
          strategies: [
            {
              type: '免费增值',
              description: '免费基础计算器，团队高级功能付费',
              pricingExample: '个人免费，团队每用户每月5美元',
              targetSegment: '个人经理和小团队'
            },
            {
              type: '一次性购买',
              description: '一次性付款终身访问',
              pricingExample: '一次性49美元无限使用',
              targetSegment: '想要简单定价的公司'
            }
          ],
          estimatedRevenue: '500个用户时月经常性收入3千-8千美元'
        },
        marketAnalysis: {
          marketSize: '会议生产力市场规模45亿美元，年增长10%',
          competitors: [
            {
              name: 'Fellow.app',
              strengths: ['全面的会议管理', '良好的集成'],
              weaknesses: ['价格昂贵', '对简单需求来说太复杂', '需要全面采用']
            },
            {
              name: 'Clockwise',
              strengths: ['AI驱动的日程安排', '日历优化'],
              weaknesses: ['不显示会议成本', '专注于日程安排而非成本', '价格昂贵']
            }
          ],
          opportunityGaps: [
            '简单的成本可视化',
            '无需复杂设置',
            '小团队负担得起'
          ]
        },
        resourceRequirements: {
          skills: [
            { skill: 'JavaScript/React', level: 'beginner', canLearn: true },
            { skill: 'UI设计', level: 'beginner', canLearn: true }
          ],
          tools: [
            { name: 'React', purpose: '前端框架', cost: '免费', alternatives: ['Vue', 'Svelte'] },
            { name: 'Vercel', purpose: '托管', cost: '有免费套餐', alternatives: ['Netlify', 'GitHub Pages'] },
            { name: 'Tailwind CSS', purpose: '样式', cost: '免费', alternatives: ['Bootstrap', 'Material-UI'] }
          ],
          estimatedCost: {
            minimum: '$0-50',
            recommended: '$50-200',
            breakdown: [
              { category: '托管', amount: '$0/月' },
              { category: '域名', amount: '$12/年' },
              { category: '分析', amount: '$0-10/月' }
            ]
          }
        },
        launchGuide: {
          totalTimeline: '1-2个月获得首笔收入',
          phases: [
            {
              phase: 1,
              name: '验证',
              duration: '3-5天',
              milestones: ['问题得到验证', '定价研究完成'],
              actions: ['调查经理', '研究会议成本', '分析竞争对手', '创建模型']
            },
            {
              phase: 2,
              name: 'MVP开发',
              duration: '1-2周',
              milestones: ['计算器功能完成', '基础UI完成'],
              actions: ['构建计算器逻辑', '创建UI', '添加导出功能', '测试计算']
            },
            {
              phase: 3,
              name: 'Beta测试',
              duration: '1周',
              milestones: ['20个Beta用户', '反馈已纳入'],
              actions: ['与经理分享', '收集反馈', '完善用户体验', '添加请求的功能']
            },
            {
              phase: 4,
              name: '支付集成',
              duration: '3-5天',
              milestones: ['支付运行', '团队功能准备就绪'],
              actions: ['集成Stripe', '构建团队仪表板', '添加用户管理', '测试支付']
            },
            {
              phase: 5,
              name: '发布',
              duration: '1周',
              milestones: ['公开发布', '首批50个用户'],
              actions: ['在Product Hunt上发布', '在LinkedIn上发帖', '在Slack社区分享', '撰写博客文章']
            }
          ]
        }
      },
      {
        title: '电子邮件模板库',
        problem: '专业人士反复重写相同类型的电子邮件，浪费时间和精力',
        solution: '智能模板系统，为常见场景提供可定制的专业电子邮件模板',
        targetAudience: '销售团队、客户服务和商务专业人士',
        targetCustomer: '销售团队、客户服务和商务专业人士',
        validationQuestion: '您花多少时间编写类似的电子邮件？',
        skillsRequired: ['内容策略', '前端开发'],
        technicalFeasibility: 'high',
        estimatedDevelopment: '2-3周完成MVP',
        revenueModel: {
          strategies: [
            {
              type: '免费增值',
              description: '免费基础模板，付费用户享有高级模板',
              pricingExample: '10个模板免费，无限模板每月9美元',
              targetSegment: '个人专业人士'
            },
            {
              type: '团队订阅',
              description: '具有共享模板库的团队计划',
              pricingExample: '最多10个用户的团队每月29美元',
              targetSegment: '小团队和部门'
            }
          ],
          estimatedRevenue: '400个用户时月经常性收入4千-1万美元'
        },
        marketAnalysis: {
          marketSize: '电子邮件生产力市场规模21亿美元，年增长11%',
          competitors: [
            {
              name: 'Grammarly',
              strengths: ['知名品牌', '良好的写作辅助'],
              weaknesses: ['不专注于模板', '高级套餐昂贵', '定制化有限']
            },
            {
              name: 'TextExpander',
              strengths: ['强大的片段系统', '跨平台'],
              weaknesses: ['对简单需求来说太复杂', '价格昂贵', '学习曲线陡峭']
            }
          ],
          opportunityGaps: [
            '电子邮件专用模板',
            '行业特定内容',
            '简单、经济实惠的解决方案'
          ]
        },
        resourceRequirements: {
          skills: [
            { skill: '内容写作', level: 'intermediate', canLearn: true },
            { skill: '前端开发', level: 'beginner', canLearn: true },
            { skill: 'UX设计', level: 'beginner', canLearn: true }
          ],
          tools: [
            { name: 'React', purpose: 'Web应用程序', cost: '免费', alternatives: ['Vue', 'Svelte'] },
            { name: 'Supabase', purpose: '数据库和身份验证', cost: '有免费套餐', alternatives: ['Firebase', 'MongoDB'] },
            { name: 'Vercel', purpose: '托管', cost: '有免费套餐', alternatives: ['Netlify', 'Railway'] }
          ],
          estimatedCost: {
            minimum: '$0-100',
            recommended: '$100-400',
            breakdown: [
              { category: '托管', amount: '$0-15/月' },
              { category: '数据库', amount: '$0-20/月' },
              { category: '域名', amount: '$12/年' },
              { category: '工具', amount: '$0-10/月' }
            ]
          }
        },
        launchGuide: {
          totalTimeline: '2-3个月获得首笔收入',
          phases: [
            {
              phase: 1,
              name: '内容研究',
              duration: '1-2周',
              milestones: ['收集50个模板想法', '类别定义完成'],
              actions: ['调查专业人士', '分析常见电子邮件类型', '定义模板类别', '创建内容计划']
            },
            {
              phase: 2,
              name: '模板创建',
              duration: '2-3周',
              milestones: ['编写30个模板', '质量审查完成'],
              actions: ['编写模板', '获取反馈', '完善内容', '添加定制选项']
            },
            {
              phase: 3,
              name: '平台开发',
              duration: '2-3周',
              milestones: ['模板浏览器构建完成', '复制功能运行'],
              actions: ['构建模板UI', '添加搜索/过滤', '实现复制功能', '添加用户账户']
            },
            {
              phase: 4,
              name: 'Beta测试',
              duration: '1-2周',
              milestones: ['30个Beta用户', '模板已验证'],
              actions: ['招募测试者', '跟踪模板使用情况', '收集反馈', '添加请求的模板']
            },
            {
              phase: 5,
              name: '货币化',
              duration: '1周',
              milestones: ['支付系统上线', '高级模板准备就绪'],
              actions: ['集成Stripe', '创建高级模板', '设置订阅', '测试支付流程']
            },
            {
              phase: 6,
              name: '发布',
              duration: '1周',
              milestones: ['公开发布', '首批100个用户'],
              actions: ['在Product Hunt上发布', '在专业社区发帖', '电子邮件营销', '内容营销']
            }
          ]
        }
      }
    ];

    const shuffled = [...allFallbackKits].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }
}
